import { useState, useContext } from "react";
import MainLayouts from "../../Layout/MainLayouts";
import { ReactComponent as Trash } from "bootstrap-icons/icons/trash3.svg";
import { ReactComponent as Eye } from "bootstrap-icons/icons/eye.svg";
import { ReactComponent as PencilSquare } from "bootstrap-icons/icons/pencil-square.svg";
import { ReactComponent as ChevronRightIcon } from "bootstrap-icons/icons/chevron-right.svg";
import { ReactComponent as ChevronDown } from "bootstrap-icons/icons/chevron-down.svg";
import { ReactComponent as Download } from "bootstrap-icons/icons/download.svg";
import { ReactComponent as Search } from "bootstrap-icons/icons/search.svg";
import { ReactComponent as ArrowClockwise } from "bootstrap-icons/icons/arrow-clockwise.svg";
import { ReactComponent as PlusSquareFill } from "bootstrap-icons/icons/plus-square-fill.svg";
import Spinner from "../spinner/Spinner";
import { Link, useNavigate } from "react-router-dom";
import { handleError } from "../../components/utils/AuthenticationCheck";
import AxiosApiCall from "../../api/AxiosApiCall";
import ApiServices from "../../api/ApiServices";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import CustomStyles from "../../components/utils/CustomStyles";
import formatDate from "../../components/utils/DateFormatter";
import { AuthContext } from "../../AuthProvider/AuthProvider";
import Swal from "sweetalert2";
import { downloadCSVFromApi } from "../../components/utils/DownlaodCSVFromApi";
import { useSecurityQuestions } from "../../hooks/useAppData";
import { useQueryClient } from "@tanstack/react-query";


const GetAllSecurityQuestion = () => {
    const [deletingId, setDeletingId] = useState(null);
    const [loadingCSV, setLoadingCSV] = useState(false);
    const [filterText, setFilterText] = useState("");
    const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState("");
    const { permissions } = useContext(AuthContext);

    const [filters, setFilters] = useState({
        start_date: "",
        end_date: "",
    });

    const navigate = useNavigate();
    const axiosInst = AxiosApiCall();
    const queryClient = useQueryClient();

    const { data: questionsResponse, isLoading: loading } = useSecurityQuestions();
    const securityQuestions = questionsResponse?.data || [];

    // Filter data by search text
    const handleFilter = (e) => {
        setFilterText(e.target.value);
        setResetPaginationToggle(!resetPaginationToggle);
    };

    // Select status filter
    const handleStatusSelect = (status) => {
        setSelectedStatus(status);
        setIsDropdownOpen(false);
    };

    // Handle delete security question
    const handleDeleteSecurityQuestion = async (questionId) => {
        const formData = { question_id: questionId };

        setDeletingId(true);
        try {
            const response = await axiosInst.delete(ApiServices.deleteSecurityQuestion, {
                data: formData,
                withCredentials: true,
            });

            const { status, message } = response.data;
            if (status === 200) {
                toast.success(message || "security question deleted successfully");
                queryClient.invalidateQueries({ queryKey: ["securityQuestions"] });
            } else {
                handleError({ response: { status, data: { message } } }, navigate);
            }
        } catch (error) {
            handleError(error, navigate);
        } finally {
            setDeletingId(false);
        }
    };

    // Handle delete confirmation
    const handleDeleteConfirmation = (questionId, questionText) => {
        Swal.fire({
            title: "Are you sure?",
            html: `You are about to delete security question:<br><strong>"${questionText}"</strong><br><br>This action cannot be undone!`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
            cancelButtonText: "Cancel",
        }).then((result) => {
            if (result.isConfirmed) {
                handleDeleteSecurityQuestion(questionId);
            }
        });
    };

    // Define Expandable Component for Actions
    const ExpandableComponent = ({ data }) => (
        <div className="flex items-center space-x-4 py-3">
            {permissions.some(permission => permission.name === 'admin.view') && (
                <div className="mx-2 my-2 group relative">
                    <Link
                        to="/admin/security/questions/details"
                        state={{ data }}
                    >
                        <Eye className="w-5 h-5 text-darkGreen3" />
                    </Link>
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 hidden group-hover:block text-darkGreen3 text-xs px-2 py-1 rounded">
                        Details
                    </div>
                </div>
            )}

            {permissions.some(permission => permission.name === 'admin.edit') && (
                <div className="mx-2 my-2 group relative">
                    <Link
                        to="/admin/security/questions/edit"
                        state={{ data }}
                    >
                        <PencilSquare className="w-5 h-5 text-dash4" />
                    </Link>
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 hidden group-hover:block text-dash4 text-xs px-2 py-1 rounded">
                        Edit
                    </div>
                </div>
            )}

            {permissions.some(permission => permission.name === 'admin.create') && (
                <div className="mx-2 my-2 group relative">
                    <button
                        onClick={() => handleDeleteConfirmation(data.id, data.question_text)}
                        disabled={deletingId === data.id}
                        className="mx-2 my-2 group"
                    >
                        {deletingId === data.id ? (
                            <ArrowClockwise className="w-5 h-5 text-dash3 animate-spin" />
                        ) : (
                            <Trash className="w-5 h-5 text-dash3 " />
                        )}
                    </button>
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-0 hidden group-hover:block text-dash3 text-xs px-2 py-0 rounded">
                        Delete
                    </div>
                </div>
            )}
        </div>
    );

    const columns = [

        {
            name: "Question",
            sortable: true,
            width: "500px",
            cell: (row) => (
                <div>
                    <span className="block font-medium text-gray-900">
                        {row.question_text || "NOT AVAILABLE"}
                    </span>
                    {row.description && (
                        <span className="block text-xs text-lGrey px-3 py-1 rounded-full bg-orange1 mt-1">
                            {row.description}
                        </span>
                    )}
                </div>
            ),
        },

        {
            name: "Status",
            selector: (row) => row.is_active ? "ACTIVE" : "INACTIVE",
            sortable: true,
            width: "120px",
            cell: (row) => {
                const isActive = row.is_active;
                const className = isActive
                    ? "px-3 py-1 rounded-full bg-darkGreen3 text-lGrey text-xs font-medium"
                    : "px-3 py-1 rounded-full bg-dash3 text-lGrey text-xs font-medium";

                return (
                    <span className={className}>
                        {isActive ? "ACTIVE" : "INACTIVE"}
                    </span>
                );
            },
        },
        {
            name: "Description",
            selector: (row) => row.description || "No description",
            sortable: true,
            width: "300px",
        },
        {
            name: "Date",
            selector: (row) => row.created_at ? formatDate(row.created_at) : "Not Available",
            sortable: true,
            width: "200px",
        },

    ];

    // Handle filter changes
    const handleFilterChange = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    // Check if both dates are selected
    const areDatesSelected = filters.start_date && filters.end_date;

    // Filter data based on search text, status, AND date range
    const filteredItems = securityQuestions.filter((item) => {
        // Filter by search text
        const matchesFilterText = Object.values(item).some(
            (value) =>
                typeof value === "string" &&
                value.toLowerCase().includes(filterText.toLowerCase())
        );

        // Filter data based on selected status
        const matchesStatus = !selectedStatus ||
            (selectedStatus === "active" && item.is_active === true) ||
            (selectedStatus === "inactive" && item.is_active === false);

        // Filter data based on date range
        let matchesDateRange = true;
        if (areDatesSelected) {
            const itemDate = new Date(item.created_at);
            const startDate = new Date(filters.start_date);
            const endDate = new Date(filters.end_date);

            // Set time to end of day for end date
            endDate.setHours(23, 59, 59, 999);

            matchesDateRange = itemDate >= startDate && itemDate <= endDate;
        }

        return matchesFilterText && matchesStatus && matchesDateRange;
    });

    const data = filteredItems.map((question) => ({
        id: question.id,
        question_text: question.question_text || "NOT AVAILABLE",
        description: question.description || "",
        sort_order: question.sort_order || "NOT AVAILABLE",
        is_active: question.is_active,
        created_at: question.created_at,
        updated_at: question.updated_at,
    }));

    // Sort data based on sort_order or creation date
    const sortedData = [...data].sort((a, b) => {
        // First by sort_order, then by created_at
        if (a.sort_order !== b.sort_order) {
            return (a.sort_order || 999) - (b.sort_order || 999);
        }
        const dateA = new Date(a.created_at);
        const dateB = new Date(b.created_at);
        return dateB - dateA; // Newest first
    });

    // Handle CSV Download - Server-side filtered by date
    const handleDownloadCSV = async () => {
        if (!areDatesSelected) {
            toast.error("Please select both start and end dates to download CSV");
            return;
        }

        // Validate date range
        const startDate = new Date(filters.start_date);
        const endDate = new Date(filters.end_date);

        if (startDate > endDate) {
            toast.error("Start date must be before end date");
            return;
        }

        // Limit date range to a year
        const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
        if (daysDiff > 365) {
            toast.error("Date range cannot exceed 1 year");
            return;
        }

        setLoadingCSV(true);

        try {
            const authData = JSON.parse(localStorage.getItem("auth"));
            const token = authData?.token;

            if (!token) {
                toast.error("No access token found");
                return;
            }

            // Prepare download payload
            const downloadPayload = {
                start_date: filters.start_date,
                end_date: filters.end_date,
                download: 1,
            };

            // Create filename with date range
            const startDateFormatted = filters.start_date.replace(/-/g, '_');
            const endDateFormatted = filters.end_date.replace(/-/g, '_');
            const filename = `security_questions_${startDateFormatted}_to_${endDateFormatted}.csv`;

            // Call the download function
            await downloadCSVFromApi(
                ApiServices.getAllSecurityQuestion,
                downloadPayload,
                token,
                {
                    filename: filename,
                    columnsToRemove: [], // Add sensitive columns to exclude
                    maxRows: 50000
                }
            );

        } catch (error) {
            // console.error("Download error:", error);
            handleError(error, navigate);
            if (error.message && error.message.includes("CSV")) {
                toast.error("Failed to process CSV file");
            } else if (error.message && error.message.includes("network")) {
                toast.error("Network error during download");
            }
        } finally {
            setLoadingCSV(false);
        }
    };

    // Clear date filters
    const handleClearDates = () => {
        setFilters({
            start_date: "",
            end_date: "",
        });
        toast.success("Date filters cleared");
    };

    // Clear all filters
    const handleClearAllFilters = () => {
        setFilterText("");
        setSelectedStatus("");
        setFilters({
            start_date: "",
            end_date: "",
        });
        toast.success("All filters cleared");
    };

    return (
        <div>
            <MainLayouts>
                <div className="sm:rounded-lg py-6 w-full">
                    {/* Breadcrumb */}
                    <section
                        className="w-full pl-[3%] pr-[3%] pt-3 my-3"
                        style={{ backgroundColor: "#F7F7F7" }}
                    >
                        <nav className="bg-gray-200 py-0 px-4 rounded-lg ">
                            <ol className="list-none p-0 inline-flex mb-2">
                                <li className="flex items-center">
                                    <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">
                                        Security
                                    </p>
                                    <ChevronRightIcon />
                                </li>
                                <li className="flex items-center">
                                    <p className="text-darkGreen3  pr-2">
                                        Questions
                                    </p>
                                    <ChevronRightIcon />
                                </li>
                                <li className="flex items-center">
                                    <p className="text-orange1">List</p>
                                </li>
                            </ol>
                        </nav>
                    </section>

                    {/* Filter Controls Section - Responsive */}
                    <div className="pb-4 bg-white dark:bg-gray-900">
                        {/* DESKTOP FILTERS (md and up) - All in one row */}
                        <div className="hidden md:flex flex-wrap items-center gap-3">
                            {/* Search Input Field with Icon */}
                            <div className="relative flex-grow min-w-[200px]">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        type="search"
                                        className="block w-full pl-10 pr-3 py-2 text-md text-gray-900 border border-gray-300 rounded-lg bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                                        placeholder="Search questions or descriptions..."
                                        value={filterText}
                                        onChange={handleFilter}
                                    />
                                </div>


                            </div>

                            {/* Status Dropdown */}
                            <div className="relative min-w-[180px]">
                                <div>
                                    {permissions.some(permission => permission.name === 'admin.view') && (
                                        <button
                                            type="button"
                                            className="bg-darkGreen3 inline-flex w-full justify-between items-center rounded-md px-3 py-2.5 text-sm font-semibold text-dGrey3"
                                            id="menu-button"
                                            aria-expanded={isDropdownOpen}
                                            aria-haspopup="true"
                                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                        >
                                            <span className="truncate">
                                                {selectedStatus === "active" ? "Active" :
                                                    selectedStatus === "inactive" ? "Inactive" :
                                                        "All Status"}
                                            </span>
                                            <ChevronDown className="w-4 h-4 text-gray-400" />
                                        </button>
                                    )}
                                </div>
                                {isDropdownOpen && (
                                    <div
                                        className="absolute right-0 z-10 mt-1 w-full origin-top-right divide-y divide-gray-100 rounded-md bg-lGrey shadow-lg ring-1 ring-black1 ring-opacity-5 focus:outline-none"
                                        role="menu"
                                        aria-orientation="vertical"
                                        aria-labelledby="menu-button"
                                    >
                                        <div
                                            className="py-1 rounded-md"
                                            role="none"
                                            style={{ backgroundColor: "white" }}
                                        >
                                            <button
                                                onClick={() => handleStatusSelect("active")}
                                                className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-darkGreen3 text-left"
                                                role="menuitem"
                                            >
                                                Active
                                            </button>
                                            <button
                                                onClick={() => handleStatusSelect("inactive")}
                                                className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-darkGreen3 text-left"
                                                role="menuitem"
                                            >
                                                Inactive
                                            </button>
                                            <button
                                                type="button"
                                                className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-darkGreen3 text-left"
                                                onClick={() => handleStatusSelect("")}
                                            >
                                                All Status
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {/* Create Security Question Button */}
                            {permissions.some(permission => permission.name === 'admin.create') && (
                                <div className="min-w-[220px]">
                                    <Link
                                        to="/admin/security/question/create"
                                        className="w-full inline-flex justify-center items-center gap-x-2 px-4 py-2.5 text-sm font-semibold text-dGrey3 bg-darkGreen3 rounded-lg hover:bg-green-700 transition-colors duration-200"
                                    >
                                        <PlusSquareFill className="w-4 h-4" />
                                        Create Security Question
                                    </Link>
                                </div>
                            )}

                            {/* Start Date Input */}
                            {/* <div className="min-w-[150px]">
                                <div className="relative">
                                    <input
                                        type="date"
                                        value={filters.start_date}
                                        onChange={(e) => handleFilterChange("start_date", e.target.value)}
                                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:ring-2 focus:ring-orange1 focus:border-transparent"
                                        max={filters.end_date || undefined}
                                        placeholder="Start Date"
                                    />
                                </div>
                            </div> */}

                            {/* End Date Input */}
                            {/* <div className="min-w-[150px]">
                                <div className="relative">
                                    <input
                                        type="date"
                                        value={filters.end_date}
                                        onChange={(e) => handleFilterChange("end_date", e.target.value)}
                                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:ring-2 focus:ring-orange1 focus:border-transparent"
                                        min={filters.start_date || undefined}
                                        placeholder="End Date"
                                    />
                                </div>
                            </div> */}

                            {/* Clear Dates Button - Only show when dates are selected */}
                            {/* {areDatesSelected && (
                                <div className="min-w-[120px]">
                                    <button
                                        onClick={handleClearDates}
                                        className="w-full inline-flex justify-center items-center gap-x-2 px-4 py-2.5 text-sm font-medium text-lGrey bg-black1 rounded-lg hover:bg-gray-800 transition-colors duration-200"
                                    >
                                        <XCircle className="w-4 h-4" />
                                        Clear Dates
                                    </button>
                                </div>
                            )} */}

                            {/* Download Button */}
                            {/* <div className="min-w-[160px]">
                                <button
                                    onClick={handleDownloadCSV}
                                    className={`w-full inline-flex justify-center items-center gap-x-2 rounded-md px-4 py-2.5 text-sm font-semibold transition-colors duration-200 ${areDatesSelected
                                        ? "bg-orange1 text-lGrey3 hover:bg-orange-600 cursor-pointer"
                                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                                        }`}
                                    disabled={!areDatesSelected || loadingCSV}
                                    title={!areDatesSelected ? "Select start and end dates to enable download" : ""}
                                >
                                    {loadingCSV ? (
                                        <>
                                            <ArrowClockwise className="w-4 h-4 text-white animate-spin" />
                                            Downloading...
                                        </>
                                    ) : (
                                        <>
                                            <Download className="w-4 h-4" />
                                            Download CSV
                                        </>
                                    )}
                                </button>
                            </div> */}

                            {/* Clear All Filters Button */}
                            {/* {(filterText || selectedStatus || areDatesSelected) && (
                                <div className="min-w-[140px]">
                                    <button
                                        onClick={handleClearAllFilters}
                                        className="w-full inline-flex justify-center items-center gap-x-2 px-4 py-2.5 text-sm font-medium text-lGrey bg-gray-600 rounded-lg hover:bg-gray-700 transition-colors duration-200"
                                    >
                                        <XCircle className="w-4 h-4" />
                                        Clear All
                                    </button>
                                </div>
                            )} */}
                        </div>

                        {/* MOBILE FILTERS (below md) - Stacked layout */}
                        <div className="md:hidden flex flex-col gap-3">
                            {/* Search - Full width on mobile */}
                            <div className="w-full">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        type="search"
                                        className="block w-full pl-10 pr-3 py-3 text-md text-gray-900 border border-gray-300 rounded-lg bg-gray-50"
                                        placeholder="Search security questions..."
                                        value={filterText}
                                        onChange={handleFilter}
                                    />
                                </div>
                            </div>

                            {/* Create Security Question Button for Mobile */}
                            {permissions.some(permission => permission.name === 'admin.create') && (
                                <div className="w-full">
                                    <Link
                                        to="/admin/security/question/create"
                                        className="w-full inline-flex justify-center items-center gap-x-2 px-4 py-3 text-sm font-semibold text-dGrey3 bg-darkGreen3 rounded-lg hover:bg-green-700"
                                    >
                                        <PlusSquareFill className="w-4 h-4" />
                                        Create Security Question
                                    </Link>
                                </div>
                            )}

                            {/* Filters grid for mobile */}
                            <div className="grid grid-cols-2 gap-3">
                                {/* Status Dropdown - Full width on mobile */}
                                <div className="col-span-2">
                                    <div>
                                        {permissions.some(permission => permission.name === 'admin.view') && (
                                            <button
                                                type="button"
                                                className="bg-darkGreen3 inline-flex w-full justify-between items-center rounded-md px-4 py-3 text-sm font-semibold text-dGrey3"
                                                id="mobile-menu-button"
                                                aria-expanded={isDropdownOpen}
                                                aria-haspopup="true"
                                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                            >
                                                <span className="truncate">
                                                    {selectedStatus === "active" ? "Active" :
                                                        selectedStatus === "inactive" ? "Inactive" :
                                                            "All Status"}
                                                </span>
                                                <ChevronDown className="w-4 h-4 text-gray-400" />
                                            </button>
                                        )}
                                    </div>
                                    {isDropdownOpen && (
                                        <div
                                            className="relative z-10 mt-1 w-full divide-y divide-gray-100 rounded-md bg-lGrey shadow-lg ring-1 ring-black1 ring-opacity-5 focus:outline-none"
                                            role="menu"
                                            aria-orientation="vertical"
                                            aria-labelledby="mobile-menu-button"
                                        >
                                            <div
                                                className="py-1 rounded-md"
                                                role="none"
                                                style={{ backgroundColor: "white" }}
                                            >
                                                <button
                                                    onClick={() => handleStatusSelect("active")}
                                                    className="block w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 hover:text-darkGreen3 text-left"
                                                    role="menuitem"
                                                >
                                                    Active
                                                </button>
                                                <button
                                                    onClick={() => handleStatusSelect("inactive")}
                                                    className="block w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 hover:text-darkGreen3 text-left"
                                                    role="menuitem"
                                                >
                                                    Inactive
                                                </button>
                                                <button
                                                    type="button"
                                                    className="block w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 hover:text-darkGreen3 text-left"
                                                    onClick={() => handleStatusSelect("")}
                                                >
                                                    All Status
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Start Date - Half width on mobile */}
                                {/* <div className="col-span-1">
                                    <div className="relative">
                                        <input
                                            type="date"
                                            value={filters.start_date}
                                            onChange={(e) => handleFilterChange("start_date", e.target.value)}
                                            className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg bg-gray-50"
                                            max={filters.end_date || undefined}
                                            placeholder="Start"
                                        />
                                    </div>
                                </div> */}

                                {/* End Date - Half width on mobile */}
                                {/* <div className="col-span-1">
                                    <div className="relative">
                                        <input
                                            type="date"
                                            value={filters.end_date}
                                            onChange={(e) => handleFilterChange("end_date", e.target.value)}
                                            className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg bg-gray-50"
                                            min={filters.start_date || undefined}
                                            placeholder="End"
                                        />
                                    </div>
                                </div> */}

                                {/* Clear Dates Button - Full width when shown */}
                                {/* {areDatesSelected && (
                                    <div className="col-span-2">
                                        <button
                                            onClick={handleClearDates}
                                            className="w-full inline-flex justify-center items-center gap-x-2 px-4 py-3 text-sm font-medium text-lGrey bg-black1 rounded-lg hover:bg-gray-800"
                                        >
                                            <XCircle className="w-4 h-4" />
                                            Clear Dates
                                        </button>
                                    </div>
                                )} */}

                                {/* Download Button - Full width on mobile */}
                                {/* <div className="col-span-2">
                                    <button
                                        onClick={handleDownloadCSV}
                                        className={`w-full inline-flex justify-center items-center gap-x-2 rounded-md px-4 py-3 text-sm font-semibold ${areDatesSelected
                                            ? "bg-orange1 text-lGrey3 hover:bg-orange-600"
                                            : "bg-gray-300 text-gray-500 cursor-not-allowed"
                                            }`}
                                        disabled={!areDatesSelected || loadingCSV}
                                    >
                                        {loadingCSV ? (
                                            <>
                                                <ArrowClockwise className="w-4 h-4 text-white animate-spin" />
                                                <span className="ml-2">Downloading...</span>
                                            </>
                                        ) : (
                                            <>
                                                <Download className="w-4 h-4" />
                                                <span className="ml-2">Download CSV</span>
                                            </>
                                        )}
                                    </button>
                                </div> */}

                                {/* Clear All Filters Button */}
                                {/* {(filterText || selectedStatus || areDatesSelected) && (
                                    <div className="col-span-2">
                                        <button
                                            onClick={handleClearAllFilters}
                                            className="w-full inline-flex justify-center items-center gap-x-2 px-4 py-3 text-sm font-medium text-lGrey bg-gray-600 rounded-lg hover:bg-gray-700"
                                        >
                                            <XCircle className="w-4 h-4" />
                                            Clear All Filters
                                        </button>
                                    </div>
                                )} */}
                            </div>
                        </div>

                        {/* DESKTOP help text (md and up) */}
                        {/* <div className="hidden md:flex items-center justify-between mt-2">
                            {!areDatesSelected && (
                                <p className="text-xs text-gray-500">
                                    Select start and end dates to enable download
                                </p>
                            )}
                            {areDatesSelected && sortedData.length > 0 && (
                                <p className="text-sm text-gray-600">
                                    Showing {sortedData.length} security questions from {filters.start_date} to {filters.end_date}
                                </p>
                            )}
                        </div> */}

                        {/* MOBILE help text (below md) */}
                        <div className="md:hidden mt-3">
                            {!areDatesSelected && (
                                <p className="text-xs text-gray-500 text-center mb-2">
                                    Select start and end dates to enable download
                                </p>
                            )}
                            {areDatesSelected && sortedData.length > 0 && (
                                <p className="text-sm text-gray-600 text-center">
                                    Showing {sortedData.length} security questions
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Data Table */}
                    {loading ? (
                        <Spinner loading={loading} />
                    ) : (
                        <DataTable
                            columns={columns}
                            data={sortedData}
                            pagination
                            striped
                            highlightOnHover
                            customStyles={CustomStyles}
                            noDataComponent={
                                <div className="text-center py-8">
                                    {areDatesSelected ?
                                        "No security questions found for the selected date range" :
                                        "No security questions found"
                                    }
                                </div>
                            }
                            expandableRows
                            expandableRowsComponent={ExpandableComponent}
                        />
                    )}
                </div>
            </MainLayouts>
        </div>
    );
};

export default GetAllSecurityQuestion;